import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react"
import { useRouter } from "next/router"

import { useAuth } from "@clerk/nextjs"
import { useSignIn } from "@clerk/nextjs/legacy"

interface Props {
  onComplete: () => void
  redirectUrlComplete: string
  invitationTicket?: string
}

export function useSigninCompletion({
  onComplete,
  redirectUrlComplete,
  invitationTicket,
}: Props) {
  const { signIn, setActive } = useSignIn()
  const { sessionId } = useAuth()
  const router = useRouter()
  const scope = useMemo(
    () => ({
      onComplete,
      redirectUrlComplete,
      invitationTicket,
      path: router.asPath,
    }),
    [onComplete, redirectUrlComplete, invitationTicket, router.asPath],
  )
  const live = useRef({ scope, signIn, sessionId })
  const mounted = useRef(false)
  const request = useRef(0)
  useLayoutEffect(() => {
    live.current = { scope, signIn, sessionId }
  })
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      request.current += 1
    }
  }, [])

  const cancelCompletion = useCallback(() => {
    request.current += 1
  }, [])

  const completeFromSession = useCallback(
    async (session: string, isCurrent: () => boolean = () => true) => {
      const attemptId = signIn?.id
      const inScope = () =>
        mounted.current &&
        live.current.scope === scope &&
        isCurrent() &&
        (!live.current.signIn?.id || live.current.signIn.id === attemptId) &&
        (!live.current.sessionId || live.current.sessionId === session)
      if (!setActive || !inScope()) {
        return
      }
      const generation = ++request.current
      const current = () => request.current === generation && inScope()
      try {
        await setActive({ session })
      } catch (error) {
        if (current()) {
          throw error
        }
        return
      }
      if (current()) {
        scope.onComplete()
      }
    },
    [scope, setActive, signIn],
  )

  return { completeFromSession, cancelCompletion }
}
